import { useEffect } from 'react';
import { translate, type Locale, type TranslationKey } from '../i18n';
import { Home } from './Home';
import { tools } from './tools';
import { version as appVersion } from '../../package.json';

type BrandMarkProps = {
  size?: number | string;
  className?: string;
  title?: string;
};

const BRAND_MARK_PATH = `
  M 405 721 L 405 826 L 425 826 L 426 724 L 425 721 Z
  M 788 449 L 767 448 L 766 465 L 760 497 L 750 531 L 740 556 L 723 589 L 706 615 L 683 644 L 661 667
  L 636 689 L 616 704 L 586 723 L 558 737 L 536 746 L 501 757 L 479 762 L 447 766 L 447 786 L 474 783
  L 503 777 L 534 768 L 565 756 L 597 740 L 617 728 L 644 709 L 671 686 L 690 667 L 712 641 L 735 608
  L 752 578 L 766 546 L 777 512 L 783 487 Z
  M 45 448 L 47 475 L 54 509 L 66 546 L 77 572 L 93 602 L 112 631 L 130 654 L 165 690 L 184 706
  L 218 730 L 244 745 L 267 756 L 298 768 L 325 776 L 353 782 L 384 786 L 383 765 L 360 763 L 331 757
  L 293 745 L 276 738 L 229 713 L 198 691 L 178 674 L 151 647 L 136 629 L 116 601 L 100 573 L 88 547
  L 73 501 L 67 471 L 65 448 Z
  M 720 408 L 720 429 L 831 429 L 831 408 Z
  M 0 408 L 0 429 L 111 429 L 111 408 Z
  M 542 365 L 513 415 L 528 422 L 555 439 L 570 451 L 595 476 L 612 502 L 615 511 L 614 519 L 610 523
  L 601 526 L 577 526 L 562 524 L 539 519 L 513 511 L 492 503 L 468 491 L 438 541 L 469 556 L 493 565
  L 533 576 L 564 581 L 615 581 L 635 577 L 656 568 L 670 556 L 676 545 L 678 536 L 678 520 L 675 506
  L 671 494 L 661 474 L 640 444 L 612 414 L 576 385 L 546 366 Z
  M 286 365 L 249 388 L 222 410 L 194 439 L 180 457 L 165 481 L 159 494 L 153 514 L 152 539 L 159 555
  L 172 567 L 195 577 L 215 581 L 252 582 L 274 580 L 297 576 L 324 569 L 360 556 L 391 541 L 361 491
  L 346 499 L 319 510 L 278 522 L 254 526 L 229 526 L 221 524 L 216 520 L 214 512 L 218 500 L 228 484
  L 245 464 L 278 436 L 295 425 L 316 414 Z
  M 295 341 L 414 541 L 533 342 Z
  M 392 154 L 376 168 L 365 182 L 356 196 L 345 218 L 334 248 L 327 275 L 323 297 L 321 323 L 372 323
  L 371 319 L 373 292 L 382 250 L 390 229 L 399 213 L 408 203 L 412 201 L 420 202 L 429 211 L 443 237
  L 454 275 L 458 304 L 458 323 L 509 323 L 507 293 L 498 251 L 486 218 L 473 193 L 463 178 L 444 158
  L 429 149 L 414 146 L 404 148 Z
  M 447 43 L 447 63 L 480 67 L 506 73 L 540 84 L 561 93 L 600 114 L 623 130 L 643 146 L 671 173
  L 702 211 L 723 244 L 737 272 L 747 296 L 758 331 L 764 360 L 767 389 L 788 389 L 780 336 L 773 310
  L 758 269 L 745 242 L 728 213 L 709 186 L 687 160 L 658 132 L 629 109 L 599 90 L 568 74 L 533 60
  L 506 52 L 471 45 Z
  M 384 43 L 361 45 L 326 52 L 302 59 L 266 73 L 238 87 L 206 107 L 180 127 L 150 155 L 128 180
  L 100 220 L 75 268 L 65 293 L 55 325 L 49 351 L 44 389 L 65 389 L 68 360 L 73 336 L 85 297 L 93 277
  L 117 231 L 143 194 L 164 170 L 188 147 L 212 128 L 231 115 L 265 96 L 295 83 L 323 74 L 353 67
  L 384 63 Z
  M 405 0 L 405 104 L 426 104 L 426 0 Z
`;

export function BrandMark({
  size = 24,
  className,
  title,
}: BrandMarkProps) {
  const labelled = Boolean(title);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 832 827"
      width={size}
      height={size}
      className={className}
      fill="none"
      role="img"
      aria-hidden={labelled ? undefined : true}
      aria-label={labelled ? title : undefined}
      focusable="false"
      preserveAspectRatio="xMidYMid meet"
    >
      {title ? <title>{title}</title> : null}
      <path
        d={BRAND_MARK_PATH}
        fill="currentColor"
        fillRule="evenodd"
        clipRule="evenodd"
      />
    </svg>
  );
}

export function AppShell({
  locale,
  setLocale,
  route,
}: {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  route: string;
}) {
  const t = (key: TranslationKey, values?: Record<string, string | number>) => translate(locale, key, values);
  const activeTool = tools.find((tool) => tool.route === route);
  const terminalWorkspace = activeTool?.id === 'aterminal';

  useEffect(() => {
    document.title = activeTool ? `${t(activeTool.nameKey)} · ATOMTEX Tools` : t('hubTitle');
  }, [activeTool, locale]);

  return (
    <div className={`app ${terminalWorkspace ? 'app--terminal-workspace' : ''}`}>
      <header className={`global-bar ${terminalWorkspace ? 'global-bar--workspace' : ''}`}>
        <a className="brand" href="#/" aria-label={t('hubTitle')}>
          <span className="brand__mark"><BrandMark /></span>
          <span className="brand__name">ATOMTEX&nbsp;<b>Tools</b></span>
        </a>

        <div className="global-bar__spacer" />

        <div className="segmented" aria-label={t('language')}>
          <button className={locale === 'ru' ? 'active' : ''} type="button" onClick={() => setLocale('ru')}>RU</button>
          <button className={locale === 'en' ? 'active' : ''} type="button" onClick={() => setLocale('en')}>EN</button>
        </div>
      </header>

      <main className={`view ${terminalWorkspace ? 'view--terminal-workspace' : ''}`}>
        {activeTool ? activeTool.render(locale) : <Home locale={locale} />}
      </main>

    {!terminalWorkspace && (
      <footer className="footer">
        <span className="footer__brand">
          <span className="footer__mark">
            <BrandMark />
          </span>

          <span>© ATOMTEX 2026</span>

          <span
            className="footer__version"
            aria-label={`Application version ${appVersion}`}
            title={`ATOMTEX Tools ${appVersion}`}
          >
            v{appVersion}
          </span>
        </span>

        <span className="footer__note">{t('localOnly')}</span>
      </footer>
    )}
    </div>
  );
}
